import React, { useEffect, Suspense } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { trackPageView } from './analytics';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Header from './components/Header';
import GradientBackground from './components/GradientBackground';
import HeroSection from './components/HeroSection';
import ProblemSection from './components/ProblemSection';
import ReportPreviewBlock from './components/ReportPreviewBlock';
import HowItWorksSection from './components/HowItWorksSection';
import WhyBetterSection from './components/WhyBetterSection';
import TrustSection from './components/TrustSection';
import PlanFormSection from './components/PlanFormSection';
import FAQSection from './components/FAQSection';
import FinalCTASection from './components/FinalCTASection';
import { I18nProvider } from './i18n/I18nContext';

const ExamplePage    = React.lazy(() => import('./pages/ExamplePage'));
const GetStartedPage = React.lazy(() => import('./pages/GetStartedPage'));

gsap.registerPlugin(ScrollTrigger);

function HomePage() {
  useEffect(() => {
    ScrollTrigger.config({ limitCallbacks: true });
    return () => {
      ScrollTrigger.getAll().forEach(t => t.kill());
    };
  }, []);

  return (
    <div className="app">
      <GradientBackground />
      <Header />
      <main>
        <HeroSection />
        <ReportPreviewBlock />
        <ProblemSection />
        <HowItWorksSection />
        <WhyBetterSection />
        <TrustSection />
        <PlanFormSection />
        <FAQSection />
        <FinalCTASection />
      </main>
    </div>
  );
}

function PageViewTracker() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);
  return null;
}

// On every navigation: scroll to the hash target if any, else reset to top.
// Polls a few frames so the target element has time to mount on the new route.
function ScrollToHash() {
  const location = useLocation();
  useEffect(() => {
    if (location.hash) {
      let attempts = 0;
      const tryScroll = () => {
        const el = document.querySelector(location.hash);
        if (el) {
          (el as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }
        if (attempts++ < 30) requestAnimationFrame(tryScroll);
      };
      tryScroll();
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);
  return null;
}

export default function App() {
  return (
    <I18nProvider>
      <ScrollToHash />
      <PageViewTracker />
      <Suspense fallback={null}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/example" element={<ExamplePage />} />
          <Route path="/get-started" element={<GetStartedPage />} />
        </Routes>
      </Suspense>
    </I18nProvider>
  );
}
